import React, { useState, useEffect, useMemo } from 'react';
import { llmConfigService, domainService } from '../../services';
import { LLMConfig, Domain } from '../../types';
import { LlmConfigModal } from '../../components/admin/LlmConfigModal';
import { FilterableTable } from '../../components/ui';
import type { FilterableColumnDef } from '../../components/ui';
import { PageContainer } from '../../components/layout';
import { UsagesModal } from '../../components/catalog/UsagesModal';
import { useCatalogUsages } from '../../components/catalog/useCatalogUsages';

export const LlmConfigsPage: React.FC = () => {
  const [configs, setConfigs] = useState<LLMConfig[]>([]);
  const [domains, setDomains] = useState<Domain[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedConfig, setSelectedConfig] = useState<LLMConfig | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [duplicateMode, setDuplicateMode] = useState(false);

  const usages = useCatalogUsages('llm-configs');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [configsData, domainsData] = await Promise.all([
        llmConfigService.list(),
        domainService.list()
      ]);
      setConfigs(configsData);
      setDomains(domainsData.filter(d => d.is_active));
    } catch (error) {
      console.error('Error loading LLM configs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (config: LLMConfig) => {
    if (!confirm(`Supprimer la configuration "${config.name}" ?`)) return;
    await usages.handleDeleteWithGuard(
      config.id,
      config.name,
      () => llmConfigService.delete(config.id),
      () => loadData()
    );
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedConfig(null);
    setDuplicateMode(false);
  };

  const handleSave = () => {
    handleModalClose();
    loadData();
  };

  const formatTemperature = (temp: number | string | null | undefined): string => {
    if (temp === null || temp === undefined) return '0.00';
    const num = typeof temp === 'string' ? parseFloat(temp) : temp;
    return isNaN(num) ? '0.00' : num.toFixed(2);
  };

  const columns: FilterableColumnDef<LLMConfig>[] = useMemo(() => [
    {
      key: 'name',
      header: 'Nom',
      sortable: true,
      searchable: true,
      render: (c) => c.name,
    },
    {
      key: 'version',
      header: 'Version',
      sortable: true,
      render: (c) => c.version,
    },
    {
      key: 'domain',
      header: 'Domaine',
      filterable: true,
      render: (c) => c.domain,
    },
    {
      key: 'model_name',
      header: 'Modèle',
      filterable: true,
      searchable: true,
      render: (c) => (
        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">{c.model_name}</span>
      ),
    },
    {
      key: 'temperature',
      header: 'Temp.',
      sortable: true,
      sortValueFn: (c) => formatTemperature(c.model_temperature),
      render: (c) => formatTemperature(c.model_temperature),
    },
    {
      key: 'tokens',
      header: 'Tokens',
      sortable: true,
      sortValueFn: (c) => String(c.model_max_tokens).padStart(10, '0'),
      render: (c) => c.model_max_tokens.toLocaleString(),
    },
    {
      key: 'usages',
      header: 'Activé dans',
      sortable: true,
      sortValueFn: (c) => String(c.tenants_count ?? 0).padStart(6, '0'),
      render: (c) => (
        <button
          onClick={() => usages.openModal(c.id, c.name)}
          className="text-blue-600 hover:underline text-sm"
        >
          {c.tenants_count ?? 0} tenants
        </button>
      ),
    },
    {
      key: 'created_by',
      header: 'Créé par',
      filterable: true,
      filterValueFn: (c) => c.created_by_name ? [c.created_by_name] : [],
      render: (c) => <span className="text-sm text-gray-500">{c.created_by_name || '-'}</span>,
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (c) => (
        <div className="flex gap-2">
          <button onClick={() => { setEditMode(true); setDuplicateMode(false); setSelectedConfig(c); setShowModal(true); }} className="text-blue-600 hover:underline text-sm">
            Modifier
          </button>
          <button onClick={() => { setEditMode(false); setDuplicateMode(true); setSelectedConfig(c); setShowModal(true); }} className="text-teal-600 hover:underline text-sm">
            Dupliquer
          </button>
          <button
            onClick={() => handleDelete(c)}
            className="text-sm text-red-600 hover:underline"
            title="Supprimer"
          >
            Supprimer
          </button>
        </div>
      ),
    },
  ], [usages]);

  return (
    <PageContainer>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Gestion des Configurations LLM</h1>
        <button
          onClick={() => { setEditMode(false); setSelectedConfig(null); setShowModal(true); }}
          className="bg-primary text-white px-4 py-2 rounded hover:bg-primary-600"
        >
          + Nouvelle
        </button>
      </div>

      {loading ? (
        <div>Chargement...</div>
      ) : (
        <FilterableTable
          persistKey="parametres-llm-configs"
          paginated
          data={configs}
          columns={columns}
          rowKey={(c) => c.id}
          defaultSort={{ key: 'name', direction: 'asc' }}
          noDataMessage="Aucune configuration LLM trouvée"
          emptyMessage="Aucune configuration ne correspond aux filtres"
        />
      )}

      {showModal && (
        <LlmConfigModal
          config={editMode ? selectedConfig : null}
          duplicateFrom={duplicateMode ? selectedConfig : null}
          domains={domains}
          onClose={handleModalClose}
          onSave={handleSave}
        />
      )}

      <UsagesModal
        open={usages.modal.open}
        catalogType="llm-configs"
        objectId={usages.modal.objectId}
        objectLabel={usages.modal.objectLabel}
        onClose={usages.closeModal}
      />
    </PageContainer>
  );
};
